import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import MediaCard from "@/components/base/MediaCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { getMediaByType } from "@/lib/media";
import { MEDIA_TYPE_LABEL, type MediaType } from "@/types/media";
import FilterBar from "@/pages/category/components/FilterBar";
import {
  applyFilter,
  buildFilterGroups,
  defaultFilterState,
  type FilterState,
} from "@/pages/category/filters";

const PAGE_SIZE = 6;

const typeIntro: Record<MediaType, string> = {
  movie: "汇集院线新作与口碑佳作，涵盖动作、爱情、科幻、悬疑等多种类型，帮你快速找到值得一看的电影。",
  tvshow:
    "收录热播剧集与完结好剧，覆盖都市、古装、悬疑、刑侦等题材，追剧不再迷路。",
  show: "精选音乐、竞技、生活、美食等各类综艺节目，轻松了解当下最受欢迎的综艺内容。",
};

function isValidType(value: string | undefined): value is MediaType {
  return value === "movie" || value === "tvshow" || value === "show";
}

export default function CategoryPage() {
  const { type } = useParams();
  const mediaType: MediaType = isValidType(type) ? type : "movie";
  const [filter, setFilter] = useState<FilterState>(defaultFilterState);
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  const sourceList = useMemo(() => getMediaByType(mediaType), [mediaType]);
  const groups = useMemo(() => buildFilterGroups(sourceList), [sourceList]);
  const filtered = useMemo(() => applyFilter(sourceList, filter), [sourceList, filter]);

  useEffect(() => {
    setFilter(defaultFilterState);
    setPage(1);
  }, [mediaType]);

  useEffect(() => {
    setPage(1);
  }, [filter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const activeCount = [
    filter.category,
    filter.region,
    filter.year,
    filter.sort,
  ].filter((v, i) => (i === 3 ? v !== "default" : v !== "all")).length;

  const label = MEDIA_TYPE_LABEL[mediaType];

  usePageMeta({
    title: `${label} - 91蜜桃`,
    description: `91蜜桃${label}频道：${typeIntro[mediaType]}`,
    keywords: `91蜜桃${label},最新${label},热门${label},${label}资源,影视资源`,
  });

  return (
    <div className="animate-fade-in">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-8 md:py-8">
          <Breadcrumb items={[{ label }]} />
          <h1 className="mt-4 font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-3xl">
            {label}
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-500">
            {typeIntro[mediaType]}
          </p>
          <p className="mt-3 text-xs text-foreground-400">
            共收录 <span className="font-bold text-primary-600">{sourceList.length}</span> 部作品
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-8">
        <button
          type="button"
          onClick={() => setFilterOpen((v) => !v)}
          className="mb-3 flex w-full items-center justify-between rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-sm font-medium text-foreground-800 md:hidden"
        >
          <span className="flex items-center gap-2">
            <i className="ri-filter-3-line text-primary-600" />
            筛选条件
            {activeCount > 0 && (
              <span className="rounded-full bg-accent-500 px-1.5 text-[11px] font-bold text-secondary-950">
                {activeCount}
              </span>
            )}
          </span>
          <i className={filterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
        </button>

        <div className={`${filterOpen ? "block" : "hidden"} md:block`}>
          <FilterBar
            groups={groups}
            state={filter}
            activeCount={activeCount}
            onChange={(key, value) => setFilter((prev) => ({ ...prev, [key]: value }))}
            onReset={() => setFilter(defaultFilterState)}
          />
        </div>

        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-foreground-500">
            筛选结果 <span className="font-bold text-foreground-900">{filtered.length}</span> 部
          </p>
        </div>

        {pageItems.length > 0 ? (
          <>
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 md:gap-5">
              {pageItems.map((item, index) => (
                <MediaCard key={item.id} item={item} index={index} />
              ))}
            </div>
            <Pagination current={page} total={totalPages} onChange={setPage} />
          </>
        ) : (
          <div className="mt-6">
            <EmptyState
              icon="ri-search-line"
              title="没有找到符合条件的作品"
              desc="试试调整筛选条件，或者重置筛选查看更多内容。"
              actionLabel="重置筛选"
              onAction={() => setFilter(defaultFilterState)}
            />
          </div>
        )}
      </div>
    </div>
  );
}